import { Container, Sprite, Texture, Loader, Graphics, IPointData } from "pixi.js"
import { Bounds, NumberedTileMeta, TileSize } from "game-common/models"
import { isTreeEntityTile } from "game-common/map/map"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { roughToFineCoordinates, setRoughToFineCoordinates } from "game-common/util"
import { CoordinatePool } from "game-common/coordinate_pool"
import { TileTextureExtractor } from "./sprites/tile_texture_extractor"
import { GrassSway } from "./effects/grass_sway"

export interface CompositeTileRendererOptions {
    surface: Container
    skySpriteSurfaceAbove: Container
    tilemapMetaProvider: TileMapMetaProvider
    compositeTileSpriteTextures: Map<string, Texture>
    surfaceObjectsByKey: Map<string, any>
    attachTexture: (texture: Texture, surface?: Container) => Sprite
    pertubation: (tileId: string, x: number, y: number, i: number, key: string, multiplier?: number) => number
    pctChance: (chance: number, key: string) => boolean
    logic: any
    tileTextureExtractor: TileTextureExtractor
    getGrassSway?: () => GrassSway | undefined
}

export class CompositeTileRenderer {
    private surface: Container
    private skySpriteSurfaceAbove: Container
    private tilemapMetaProvider: TileMapMetaProvider
    private compositeTileSpriteTextures: Map<string, Texture>
    private surfaceObjectsByKey: Map<string, any>
    private attachTexture: (texture: Texture, surface?: Container) => Sprite
    private pertubation: (tileId: string, x: number, y: number, i: number, key: string, multiplier?: number) => number
    private pctChance: (chance: number, key: string) => boolean
    private logic: any
    private tileTextureExtractor: TileTextureExtractor
    // Read lazily rather than held: the renderer that owns the wind is built after this one.
    private getGrassSway: () => GrassSway | undefined

    constructor(options: CompositeTileRendererOptions) {
        this.surface = options.surface
        this.skySpriteSurfaceAbove = options.skySpriteSurfaceAbove
        this.tilemapMetaProvider = options.tilemapMetaProvider
        this.compositeTileSpriteTextures = options.compositeTileSpriteTextures
        this.surfaceObjectsByKey = options.surfaceObjectsByKey
        this.attachTexture = options.attachTexture
        this.pertubation = options.pertubation
        this.pctChance = options.pctChance
        this.logic = options.logic
        this.tileTextureExtractor = options.tileTextureExtractor
        this.getGrassSway = options.getGrassSway || (() => undefined)
    }

    public updateTilemapMetaProvider(tilemapMetaProvider: TileMapMetaProvider): void {
        this.tilemapMetaProvider = tilemapMetaProvider
    }

    // Plant art does not own pointer events. Desktop strikes can still resolve a blade to its
    // root by checking its geometry separately from Pixi's interactive hit test.
    public updateCompositeTilePointer = (at: IPointData) => {
        let root: { x: number; y: number } | undefined
        let topZ = -Infinity
        for (const object of this.surfaceObjectsByKey.values()) {
            if (!object.compositeRoot) continue
            for (const sprite of object.spriteSet as Sprite[]) {
                if (sprite.parent && sprite.worldVisible && sprite.zIndex >= topZ && sprite.containsPoint(at)) {
                    root = object.compositeRoot
                    topZ = sprite.zIndex
                }
            }
        }
        this.logic.playerController.updateCompositeTile(root?.x ?? -1, root?.y ?? -1)
    }

    public renderCompositeTile = (
        tilemapMetaProvider: TileMapMetaProvider,
        parentTileId: string,
        compositeTileId: string,
        x: number,
        y: number,
        zIndex: number = 0,
    ) => {
        const key = `${parentTileId}-${x}-${y}`
        const parentTileMeta = tilemapMetaProvider.resolveTileMeta(parentTileId)

        //
        // dense microtiles
        //
        if (parentTileMeta.tileTypes.includes("dense")) {
            this.renderDenseMicrotiles(key, parentTileId, parentTileMeta, x, y, zIndex)
            return
        }

        //
        // compositeTile
        //

        const tileset = tilemapMetaProvider.resolveTileSet(parentTileId)

        const compositeSprites = this.handleRenderCompositeTile(
            key,
            tilemapMetaProvider,
            compositeTileId,
            tileset.tilesetId,
            x,
            y,
            this.surface,
            zIndex,
        )
    }

    private handleRenderCompositeTile = (
        key: string,
        tilemapMetaProvider: TileMapMetaProvider,
        compositeTileId: string,
        tilesetId: string,
        x: number = 0,
        y: number = 0,
        surface?: Container,
        extraZIndex?: number,
    ): Sprite[] => {
        let set: Sprite[] = this.surfaceObjectsByKey.get(key)?.spriteSet
        if (set) {
            return set
        }
        set = []

        const compositeTileMeta = tilemapMetaProvider.resolveCompositeTileIdTileMeta(compositeTileId, tilesetId)
        if (!compositeTileMeta) {
            return set
        }

        const parentTileSet = tilemapMetaProvider.getTilesetById(tilesetId)
        const parentTileMeta = compositeTileMeta.tileMeta

        //
        // compositeTile
        //
        const { width } = parentTileSet

        // find the parent tile location
        let parentTileSetX
        let parentTileSetY

        const tileNumber = compositeTileMeta.tileNumber - 1
        parentTileSetX = tileNumber % width
        parentTileSetY = Math.floor(tileNumber / width)

        const isDenseVertical = parentTileMeta.tileTypes.includes("denseVertical")
        if (isDenseVertical) {
            this.renderDenseVerticalTiles(
                key,
                compositeTileMeta,
                parentTileSet,
                parentTileSetX,
                parentTileSetY,
                x,
                y,
                surface,
                extraZIndex,
                set,
            )
            this.surfaceObjectsByKey.set(key, {
                spriteSet: set,
                compositeTileId,
                tilesetId,
                compositeRoot: { x, y },
            })
        } else {
            const fullTileId = `${parentTileSet.tilesetId}-${compositeTileId}`

            const texture = this.tileTextureExtractor.extract(
                tilemapMetaProvider,
                parentTileSet.tilesetId,
                compositeTileId,
            )

            const bounds = TileTextureExtractor.compositeTileSpriteBounds.get(fullTileId)
            const children: NumberedTileMeta[] = compositeTileMeta.children || []
            let intersects = false
            for (let i = 0; i < children.length; i++) {
                const childTileMeta = children[i]
                const tileNumber = childTileMeta.tileNumber - 1
                const tileSetX = tileNumber % width
                const tileSetY = Math.floor(tileNumber / width)

                const childTileSetXOffset = tileSetX - parentTileSetX
                const childTileSetYOffset = tileSetY - parentTileSetY

                const xx = x + childTileSetXOffset
                const yy = y + childTileSetYOffset

                if (tilemapMetaProvider.isSkyTileAt(xx, yy)) {
                    intersects = true
                    break
                }
            }

            if (!intersects) {
                this.renderNonIntersectingCompositeTile(
                    compositeTileMeta,
                    texture,
                    bounds,
                    x,
                    y,
                    surface,
                    extraZIndex,
                    set,
                )
            } else {
                this.renderSkyIntersectingCompositeTile(
                    compositeTileMeta,
                    parentTileSet,
                    x,
                    y,
                    surface,
                    extraZIndex,
                    set,
                    bounds,
                    children,
                    tilemapMetaProvider,
                )
            }

            this.surfaceObjectsByKey.set(key, {
                spriteSet: set,
                compositeTileId,
                tilesetId,
            })
        }

        // Tag the tree tiles so the occluder-fade pass can fade ONLY trees (a structured check on the
        // tile's entityTileId -- no sprite/texture name parsing). Pool sprites default to false at
        // checkout (SpriteManagementService.attachTexture), so this only needs to flip trees on.
        const entityTileId = parentTileMeta?.entityTileId
        if (entityTileId && isTreeEntityTile(entityTileId)) {
            for (const g of set) {
                ;(g as any).occluderTreeTile = true
            }
        }

        return set
    }

    public renderDenseMicrotiles = (
        key: string,
        parentTileId: string,
        parentTileMeta: any,
        x: number,
        y: number,
        zIndex: number,
    ) => {
        const set = []
        this.surfaceObjectsByKey.set(key, {
            spriteSet: set,
        })

        const fullTileId = this.tilemapMetaProvider.resolveTilesetTileId(parentTileId)
        let textTure = this.compositeTileSpriteTextures.get(fullTileId)
        if (!textTure) {
            if (parentTileMeta.spriteId) {
                const loader = Loader.shared
                let resource = loader.resources["humanoid"]
                if (resource) {
                    textTure = resource.texture
                }
            } else {
                textTure = Texture.from(`${fullTileId}`)
                this.compositeTileSpriteTextures.set(fullTileId, textTure)
            }
        }

        const parentFine = roughToFineCoordinates({ x, y })
        const noiseChance = 0.1

        const print = (offsetX: number = 0, offsetY: number = 0) => {
            const g = this.attachTexture(textTure)
            g.x = parentFine.x + offsetX
            g.y = parentFine.y + offsetY
            g.zIndex = zIndex
            set.push(g)
        }

        print()
        this.pctChance(noiseChance, `pct-${key}-${parentFine.x}-${parentFine.y}-${zIndex}`) &&
            print(-0.5 * TileSize, -0.5 * TileSize)
        this.pctChance(noiseChance, `pct-${key}-${parentFine.x}-${parentFine.y}-${zIndex}`) &&
            print(0.5 * TileSize, -0.5 * TileSize)
        this.pctChance(noiseChance, `pct-${key}-${parentFine.x}-${parentFine.y}-${zIndex}`) &&
            print(-0.5 * TileSize, 0.5 * TileSize)
        this.pctChance(noiseChance, `pct-${key}-${parentFine.x}-${parentFine.y}-${zIndex}`) &&
            print(0.5 * TileSize, 0.5 * TileSize)
    }

    public renderDenseVerticalTiles = (
        key: string,
        compositeTileMeta: any,
        parentTileSet: any,
        parentTileSetX: number,
        parentTileSetY: number,
        x: number,
        y: number,
        surface: Container | undefined,
        extraZIndex: number | undefined,
        set: Sprite[],
    ) => {
        const count = this.pctChance(10, `dense-vertical-${key}-1`)
            ? this.pctChance(50, `dense-vertical-${key}-2`)
                ? 1
                : 3
            : 2
        const gradient = TileSize / count

        for (let i = 0; i < count; i++) {
            let verticalOffset = i * gradient
            const yPertubation = this.pertubation("_", x, y, i, `horizontalDensity`)

            const children: NumberedTileMeta[] = compositeTileMeta.children || []
            const parentTileIdNumber = compositeTileMeta.tileNumber
            // The id the composite's parent tile renders under. NOTE the convention change: a tile's
            // position as an AUTHOR writes it is one below the id the client resolves it to (see
            // flora.ts), but compositeTileMeta.tileNumber is already in the resolved convention. Both
            // numbers are small, plausible and name a real neighbouring tile, so getting this wrong
            // matches nothing and reports nothing -- which is exactly what it did first.
            const parentCompositeTextureId = `${parentTileSet.tilesetId}-${parentTileIdNumber}`

            let height = 0
            children.forEach(childTileMeta => {
                const tileId = `${childTileMeta.tileNumber}`
                const tileNumber = childTileMeta.tileNumber - 1
                const tileSetX = tileNumber % parentTileSet.width
                const tileSetY = Math.floor(tileNumber / parentTileSet.width)

                const fullTileId = `${parentTileSet.tilesetId}-${tileId}`

                const isParent = parentTileIdNumber === tileNumber
                const childTileSetXOffset = tileSetX - parentTileSetX
                const childTileSetYOffset = tileSetY - parentTileSetY
                if (!isParent) {
                    height = Math.max(height, Math.abs(childTileSetYOffset))
                }

                let textTure = this.compositeTileSpriteTextures.get(fullTileId)
                if (!textTure) {
                    textTure = Texture.from(`${fullTileId}`)
                    this.compositeTileSpriteTextures.set(fullTileId, textTure)
                }
                const g = this.attachTexture(textTure, surface)

                const fineItem = CoordinatePool.instance.checkout()
                const roughItem = CoordinatePool.instance.checkout()
                const rough = roughItem.data
                rough.x = x + childTileSetXOffset
                rough.y = y + childTileSetYOffset
                const fine = fineItem.data
                setRoughToFineCoordinates(rough, fine)

                g.x = fine.x - this.pertubation(tileId, fine.x, fine.y, i, `verticalDensity`, 1)
                g.y = fine.y - yPertubation
                g.zIndex =
                    100 +
                        roughToFineCoordinates({ x, y }).y -
                        verticalOffset -
                        yPertubation +
                        TileSize / 3 +
                        extraZIndex || 0

                CoordinatePool.instance.release(roughItem)
                CoordinatePool.instance.release(fineItem)

                g.interactive = false

                // Offer this sub-tile to the wind. Long grass is a two-tile vertical composite -- a
                // base and a top -- so the sub-tile's own height above the anchor row is exactly how
                // much of the bend it should carry: the base barely moves and the top does the
                // leaning, which is how a plant bends rather than how a decal slides.
                //
                // Keyed on the COMPOSITE's parent tile, not on the sub-tile, so one entry in the sway
                // table covers every piece of the plant.
                this.getGrassSway()?.register(g, x, y, parentCompositeTextureId, -childTileSetYOffset)

                set.push(g)
            })
            if (height > 0 || compositeTileMeta.tileMeta.renderZIdxOffset !== undefined) {
                set.forEach(g => {
                    if (height > 1) {
                        g.y += TileSize
                        g.zIndex += TileSize
                    }
                    if (compositeTileMeta.tileMeta.renderZIdxOffset) {
                        g.zIndex += compositeTileMeta.tileMeta.renderZIdxOffset
                    }
                })
            }
        }
    }

    public renderNonIntersectingCompositeTile = (
        compositeTileMeta: any,
        texture: Texture,
        bounds: Bounds,
        x: number,
        y: number,
        surface: Container | undefined,
        extraZIndex: number | undefined,
        set: Sprite[],
    ) => {
        const surfaceToUse = surface || this.surface
        const containingSprite: Sprite = this.attachTexture(texture, surfaceToUse)
        const fine = roughToFineCoordinates({ x, y: y - bounds.height + 1 })

        containingSprite.x =
            fine.x -
            (bounds.width > 1 ? bounds.width * 0.25 * TileSize : 0) +
            (compositeTileMeta?.tileMeta?.renderXOffset || 0)
        containingSprite.y = fine.y + (compositeTileMeta?.tileMeta?.renderYOffset || 0)
        containingSprite.zIndex = 100 + (extraZIndex || 0) + (fine.y + bounds.height * TileSize) - TileSize * 0.75

        set.push(containingSprite)
    }

    public renderSkyIntersectingCompositeTile = (
        compositeTileMeta: any,
        parentTileSet: any,
        x: number,
        y: number,
        surface: Container | undefined,
        extraZIndex: number | undefined,
        set: Sprite[],
        bounds: Bounds,
        children: NumberedTileMeta[],
        tilemapMetaProvider: TileMapMetaProvider,
    ) => {
        // find the parent tile location
        const tileNumber = compositeTileMeta.tileNumber - 1
        const parentTileSetXLocal = tileNumber % parentTileSet.width
        const parentTileSetYLocal = Math.floor(tileNumber / parentTileSet.width)

        const fineXX = roughToFineCoordinates({ x, y }).x
        const fineYY = roughToFineCoordinates({ x, y }).y

        children.forEach(childTileMeta => {
            const tileId = `${childTileMeta.tileNumber}`
            const tileNumber = childTileMeta.tileNumber - 1
            const tileSetX = tileNumber % parentTileSet.width
            const tileSetY = Math.floor(tileNumber / parentTileSet.width)

            const fullTileId = `${parentTileSet.tilesetId}-${tileId}`
            const childTileSetXOffset = tileSetX - parentTileSetXLocal
            const childTileSetYOffset = tileSetY - parentTileSetYLocal

            const xx = x + childTileSetXOffset
            const yy = y + childTileSetYOffset

            const skyAt = tilemapMetaProvider.isSkyTileAt(xx, yy)
            const surfaceToUse = skyAt ? this.skySpriteSurfaceAbove : surface
            const subtileSprite = this.attachTexture(Texture.from(fullTileId), surfaceToUse)
            const fine = roughToFineCoordinates({ x: xx, y: yy })

            const fineX = fine.x
            const fineY = fine.y + (compositeTileMeta?.tileMeta?.renderYOffset || 0)

            subtileSprite.x = fineX
            subtileSprite.y = fineY

            subtileSprite.zIndex = +(extraZIndex || 0) + (fineYY + bounds.height * TileSize) - TileSize

            set.push(subtileSprite)

            if (false) {
                if (!skyAt) {
                    {
                        const g = new Graphics()
                        g.beginFill(0xfffffff)
                        g.drawCircle(16, 16, 5)
                        g.zIndex = 10000000
                        g.endFill()
                        g.x = fineXX
                        g.y = subtileSprite.zIndex - 100
                        surfaceToUse.addChild(g)
                    }

                    const g = new Graphics()
                    g.beginFill(0xff0000)
                    g.drawCircle(16, 16, 5)
                    g.zIndex = 10000000
                    g.endFill()
                    g.x = fineX
                    g.y = fineY
                    surfaceToUse.addChild(g)
                } else {
                    const g = new Graphics()
                    g.beginFill(0x00ff00)
                    g.drawCircle(16, 16, 5)
                    g.zIndex = 10000000
                    g.endFill()
                    g.x = fineX
                    g.y = fineY
                    surfaceToUse.addChild(g)
                }
            }
        })
    }

    updateSkySurfaces = (skySpriteSurfaceAbove: Container) => {
        this.skySpriteSurfaceAbove = skySpriteSurfaceAbove
    }
}
